import HttpService from './http.service';
import { API_CONFIG } from '../config/api.config';

// Definición de tipos para las peticiones y respuestas
export interface Message {
  role: 'user' | 'assistant' | 'system';
  content: string;
}

export interface ChatRequest {
  messages: Message[];
  model?: string;
  temperature?: number;
  max_tokens?: number;
}

export interface ChatResponse {
  conversationId: string;
  messageId: string;
  content: string;
  role: 'assistant' | 'user' | 'system';
  timestamp: string;
  messageType: string;
  additionalData: any | null;
  companyRecommendations: any | null;
}

export interface ConversationHistory {
  id: string;
  title: string;
  messages: Message[];
  createdAt: string;
  updatedAt: string;
}

// Servicio para interactuar con la API de OpenAI a través del backend
const OpenAIService = {
  /**
   * Verifica los claims del token JWT actual
   * @returns Promesa con los claims del token
   */
  debugTokenClaims: async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        throw new Error('No hay token de autenticación disponible');
      }

      const response = await HttpService.get('/api/AI/debug-claims', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  /**
   * Inicia una nueva conversación con el primer mensaje del usuario
   * @param message - Primer mensaje del usuario
   * @param options - Opciones adicionales para la petición
   * @returns Promesa con la respuesta que incluye el ID de la conversación y la respuesta del asistente
   */
  startConversation: async (
    message: string,
    options?: {
      model?: string;
      temperature?: number;
      max_tokens?: number;
    }
  ): Promise<ChatResponse> => {
    try {
      const payload = {
        message,
        ...options
      };

      // Asegurarse de que la URL es correcta
      const url = '/api/AI/conversations';

      // Verificar que el token existe antes de hacer la petición
      const token = localStorage.getItem('auth_token');
      if (!token) {
        throw new Error('No hay token de autenticación disponible');
      }

      // Usar el timeout específico para llamadas a OpenAI
      const response = await HttpService.post<ChatResponse>(url, payload, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        timeout: API_CONFIG.OPENAI_TIMEOUT // Usar timeout extendido para OpenAI
      });

      return response.data;
    } catch (error: any) {
      throw error;
    }
  },

  sendMessage: async (
    conversationId: string,
    message: string,
    options?: {
      model?: string;
      temperature?: number;
      max_tokens?: number;
    }
  ): Promise<ChatResponse> => {
    try {
      const payload = {
        message,
        ...options
      };

      // Asegurarse de que la URL es correcta
      const url = `/api/AI/conversations/${conversationId}/messages`;
      // Verificar que el token existe antes de hacer la petición
      const token = localStorage.getItem('auth_token');
      if (!token) {
        throw new Error('No hay token de autenticación disponible');
      }

      // Usar el timeout específico para llamadas a OpenAI
      const response = await HttpService.post<ChatResponse>(url, payload, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        timeout: API_CONFIG.OPENAI_TIMEOUT // Usar timeout extendido para OpenAI
      });

      return response.data;
    } catch (error: any) {
      throw error;
    }
  },

  getConversationHistory: async (): Promise<ConversationHistory[]> => {
    try {
      const response = await HttpService.get<ConversationHistory[]>('/api/openai/conversations');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getConversation: async (conversationId: string): Promise<ConversationHistory> => {
    try {
      const response = await HttpService.get<ConversationHistory>(`/api/openai/conversations/${conversationId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  createConversation: async (title: string): Promise<ConversationHistory> => {
    try {
      const response = await HttpService.post<ConversationHistory>('/api/openai/conversations', { title });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  updateConversation: async (
    conversationId: string,
    data: { title?: string; messages?: Message[] }
  ): Promise<ConversationHistory> => {
    try {
      const response = await HttpService.put<ConversationHistory>(
        `/api/openai/conversations/${conversationId}`,
        data
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  deleteConversation: async (conversationId: string): Promise<void> => {
    try {
      await HttpService.delete(`/api/openai/conversations/${conversationId}`);
    } catch (error) {
      throw error;
    }
  },

  addMessageToConversation: async (
    conversationId: string,
    message: Message
  ): Promise<ChatResponse> => {
    try {
      const response = await HttpService.post<ChatResponse>(
        `/api/openai/conversations/${conversationId}/messages`,
        { message }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

export default OpenAIService;
